import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SEO from '@/components/SEO';
import { Phone, Mail, MapPin, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Contact Southern Bullion Exchange in Bishopdale, Christchurch. Visit our store, call 021 153 1763 or (03) 359 6060, or send us a message. Expert bullion advice for NZ investors."
        path="/contact"
      />
      <section className="bg-[#0A0A0A] py-16 text-center">
        <div className="max-w-7xl mx-auto px-6">
          <p className="section-label mb-3">Get In Touch</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "Cinzel, serif" }}>Contact Us</h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Have questions about buying or selling precious metals? We're here to help.
          </p>
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            {submitted ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Message Sent</h3>
                <p className="text-gray-500">We'll respond within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="bg-white" data-testid="contact-name" /></div>
                  <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="bg-white" data-testid="contact-email" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-white" data-testid="contact-phone" /></div>
                  <div><Label>Subject</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="bg-white" data-testid="contact-subject" /></div>
                </div>
                <div><Label>Message *</Label><Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={5} required className="bg-white" data-testid="contact-message" /></div>
                <Button type="submit" disabled={submitting} className="btn-gold w-full" data-testid="contact-submit">
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            )}
          </div>
          <div className="space-y-6">
            <div className="bg-[#FAFAFA] p-8 rounded-sm">
              <h3 className="text-lg font-bold mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3"><MapPin size={20} className="text-[#D4AF37] mt-0.5 flex-shrink-0" /><div><p className="font-semibold text-sm">Address</p><p className="text-sm text-gray-500">Shop 9 Bishopdale Court, Bishopdale 8053, Christchurch</p></div></div>
                <div className="flex items-start gap-3"><Phone size={20} className="text-[#D4AF37] mt-0.5 flex-shrink-0" /><div><p className="font-semibold text-sm">Phone</p><a href="tel:0211531763" className="block text-sm text-gray-500 hover:text-[#D4AF37]">021 153 1763</a><a href="tel:033596060" className="block text-sm text-gray-500 hover:text-[#D4AF37]">(03) 359 6060</a></div></div>
                <div className="flex items-start gap-3"><Mail size={20} className="text-[#D4AF37] mt-0.5 flex-shrink-0" /><div><p className="font-semibold text-sm">Email</p><a href="mailto:southernbullionnz@gmail.com" className="block text-sm text-gray-500 hover:text-[#D4AF37]">southernbullionnz@gmail.com</a><a href="mailto:joe@southernbullionex.co.nz" className="block text-sm text-gray-500 hover:text-[#D4AF37]">joe@southernbullionex.co.nz</a><a href="mailto:ethan@southernbullionex.co.nz" className="block text-sm text-gray-500 hover:text-[#D4AF37]">ethan@southernbullionex.co.nz</a></div></div>
                <div className="flex items-center gap-3"><Clock size={20} className="text-[#D4AF37] flex-shrink-0" /><div><p className="font-semibold text-sm">Hours</p><p className="text-sm text-gray-500">Monday - Friday: 9am - 5pm<br/>Saturday: By appointment</p></div></div>
              </div>
            </div>
            <div className="rounded-sm overflow-hidden h-64 bg-gray-100">
              <iframe
                title="Southern Bullion Exchange Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2891.5!2d172.5726!3d-43.4984!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sBishopdale%20Court%2C%20Christchurch!5e0!3m2!1sen!2snz!4v1"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
